import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Module, Plan } from '@oneqrew-partnertool/models';
import { selectFetchedPlans } from '@oneqrew-partnertool/core';
import { PLANS_HELPER } from './plan-data.service';
@Injectable({
  providedIn: 'root'
})
export class PlanCalculatorService {
  private cachedDeduction = 0;
  private extraModules: any[] = [];
  private fetchedPlansMap: Map<string, number[]> = new Map();

  constructor(private store: Store) {
    this.initialize();
  }

  private readonly legacyDiscountOrderDateCutoff = '2026-07-31';
  private readonly legacyDiscountStartDateCutoff = '2026-09-01';
  private readonly legacyDiscountRate = 30;

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isWithinLegacyDiscountWindow(startDate: Date): boolean {
    const orderedAtDate = this.formatLocalDate(new Date());
    const formattedStartDate = this.formatLocalDate(startDate || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
    return orderedAtDate <= this.legacyDiscountOrderDateCutoff && formattedStartDate <= this.legacyDiscountStartDateCutoff;
  }

  resolveDiscount(plan: Plan): number {
    if (this.isWithinLegacyDiscountWindow(plan.startDate)) {
      return this.legacyDiscountRate;
    } else {
      return plan.discount;
    }
  }

  getRebate(plan: Plan): number {
    const rebatePercent = this.calcDiscount(plan) || 0.2;
    return this.getSubtotal(plan) * rebatePercent;
  }

  calcDiscount(plan: Plan): number {
    return this.resolveDiscount(plan) / 100;
  }

  getTotal(plan: Plan, isNewCustomer: boolean = false): number {
    const rebate = isNewCustomer ? 0 : this.getRebate(plan);
    return (
      this.getSubtotal(plan) +
      this.getCloudVersionCost(plan.cloudVersion, plan.workStationCount) -
      rebate +
      this.getTotalDeviceCost(plan.devicesCount)
    );
  }

  getNewPlanTotal(plan: Plan, discount: number): number {
    const subTotal = this.getSubtotal(plan);
    return (
      subTotal +
      this.getCloudVersionCost(plan.cloudVersion, plan.workStationCount) -
      subTotal * ((discount || 0) / 100) +
      this.getTotalDeviceCost(plan.devicesCount)
    );
  }

  getTotalWithoutCloudVersion(plan: Plan): number {
    return this.getSubtotal(plan) - this.getRebate(plan);
  }

  getSubtotalWithAddedDevices(plan: Plan): number {
    return this.getSubtotal(plan);
  }

  getSubtotal(plan: Plan): number {
    const T = plan.price; // Treaty base price
    const M = this.getAdditionalModulesPrice(plan); // Sum of other additional modules
    const N = plan.workStationCount; // Number of workspace users
    // Full formula calculation
    let total: number;
    if (N === 0) {
      // If there are no workstations, use a simplified formula
      total = T + M + this.getTotalWorkStationCostWithModule(plan) + this.getClientsCost(plan.clientsCount);
    } else {
      // If there are workstations, apply the full formula
      total = this.getTotalWorkStationCostWithModule(plan) + this.getClientsCost(plan.clientsCount) + T + M;
    }

    return total;
  }

  getPlanItemstotal(plan: Plan): number {
    return plan.price + this.getTotalDeviceCost(plan.devicesCount) + this.getTotalWorkStationCost(plan.planType, plan.workStationCount);
  }

  getTotalDeviceCost(deviceCount: number) {
    const devicePrice = 4.9;
    return deviceCount * devicePrice;
  }

  getCloudVersionCost(cloudVersion: boolean, workStationCount: number) {
    return cloudVersion ? 40 + workStationCount * 20 : 0;
  }

  getTotalWorkStationCostWithModule(plan: Plan): number {
    return this.getTotalWorkStationCostDisplay(plan) * plan.workStationCount;
  }

  getTotalWorkStationCostDisplay(plan: Plan): number {
    return (plan.price + this.getClientsCost(plan.clientsCount) + this.getAdditionalModulesPrice(plan)) / 2;
  }

  getClientsCost(clientsCount: number) {
    const clientPrice = 20;
    return clientsCount * clientPrice;
  }

  // getTotalWorkStationCost(planType: string, workstation: number) {
  //   const workstationPrice = planType === 'smarthandwerk pro L-Paket' ? 64.5 : 39.5;
  //   return workstation * workstationPrice;
  // }

  getTotalWorkStationCost(planType: string, workstation: number): number {
    let workstationPrice = 0;

    this.store
      .select(selectFetchedPlans)
      .pipe(take(1))
      .subscribe((fetchedPlans) => {
        const matchedPlan = fetchedPlans.find((plan) => plan.planType === planType);
        if (matchedPlan) {
          workstationPrice = matchedPlan.price / 2 || 0;
        } else {
          console.warn(`Plan not found for type: ${planType}`);
        }
      });

    return workstation * workstationPrice;
  }

  private initialize(): void {
    this.store
      .select(selectFetchedPlans)
      .pipe(take(1))
      .subscribe((fetchedPlans) => {
        fetchedPlans.forEach((p) => this.fetchedPlansMap.set(p.packageType, p.additionalModules?.map((m) => m.id) ?? []));
        if (!fetchedPlans || fetchedPlans.length === 0) {
          console.warn('Fetched Plans are empty. Ensure the store is updated before this component initializes.');
        } else {
          const selectedPlan = fetchedPlans[1];
          if (selectedPlan && selectedPlan.additionalModules) {
            this.extraModules = selectedPlan.additionalModules;
            this.cachedDeduction = this.extraModules.reduce((total, module) => total + (module.monthlyPrice || 0), 0);
          } else {
            console.warn('No additionalModules found in the selected plan.');
          }
        }
      });
  }

  private filterAdditionalModules(plan: Plan): Module[] {
    const defaultAdditionalModules = this.fetchedPlansMap.get(plan.packageType) ?? [];
    return plan.additionalModules?.filter((m) => !defaultAdditionalModules.includes(m.id)) ?? [];
  }

  getAdditionalModulesPrice(plan: Plan): number {
    // filter additional modules in a plan that is an extra.
    const filterAdditionalModules = this.filterAdditionalModules(plan);
    let additionalModulesCost = filterAdditionalModules.reduce((total, module) => total + (module.price || 0), 0);
    // Ensure total cost doesn't go below 0
    return Math.max(additionalModulesCost, 0);
  }

  getModuleCalculatedPrice(modulePrice: number, moduleName: string, workstationCount: number, planType: string) {
    const isExtraModule = this.extraModules.some((module) => module.name === moduleName);

    // Saving logic incase to be reverted
    // if (planType === 'smarthandwerk pro L-Paket' && isExtraModule) {
    //   // plan L
    //   if (workstationCount !== 0) {
    //     price += workstationCount * (modulePrice / 2) + modulePrice - modulePrice;
    //   } else {
    //     price = 0;
    //   }
    // } else {
    //   // Plan M
    //   if (workstationCount !== 0) {
    //     price += workstationCount * (modulePrice / 2) + modulePrice;
    //   } else {
    //     price = modulePrice;
    //   }
    // }

    if (PLANS_HELPER.isL_PAKET(planType) && isExtraModule) {
      modulePrice = 0;
    }

    return modulePrice;
  }

  getDiscountedPlanPrice(plan: Plan): number {
    let discount = this.getPlanPrice(plan) * this.calcDiscount(plan) || 0.3;
    return this.getPlanPrice(plan) - discount;
  }

  getPlanPrice(plan: Plan): number {
    return plan.price;
  }
}
